import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, Select, Button } from '../ui';
import { useApp } from '../../contexts/AppContext';
import { getSettingsByOrganization } from '../../services/settingsService';
import {
  getOrgStatsRealtime,
  getMonthlyDebit,
  getMonthlyCredit,
  getMonthlyExpenses,
  migrateAcademyIds,
} from '../../services/statsService';
import { getReceiptsByOrganization, cleanOrphanedReceipts } from '../../services/receiptService';
import { getPlayersByOrganization } from '../../services/playerService';
import { getAcademiesByOrganization } from '../../services/academyService';
import { getUserById } from '../../services/userService';
import {
  OrgStats,
  MonthlyDebitDoc,
  MonthlyCreditDoc,
  MonthlyExpenseDoc,
  Player,
  Receipt,
  Academy,
  User,
} from '../../types';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

const COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];

const Stats: React.FC = () => {
  const { selectedAcademy, selectedOrganization } = useApp();
  const [defaultCurrency, setDefaultCurrency] = useState<string>('USD');

  // Section 1: Overview (real-time)
  const [overviewStats, setOverviewStats] = useState<OrgStats | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(true);

  // Section 2: Monthly data
  const now = new Date();
  const currentMonthStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const [selectedMonth, setSelectedMonth] = useState<string>(currentMonthStr);
  const [monthlyDebit, setMonthlyDebit] = useState<MonthlyDebitDoc | null>(null);
  const [monthlyCredit, setMonthlyCredit] = useState<MonthlyCreditDoc | null>(null);
  const [monthlyExpenses, setMonthlyExpenses] = useState<MonthlyExpenseDoc | null>(null);
  const [monthlyLoading, setMonthlyLoading] = useState(true);

  // Report modal state
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedMonths, setSelectedMonths] = useState<string[]>([]);
  const [reportStatus, setReportStatus] = useState('all');
  const [generatingReport, setGeneratingReport] = useState(false);
  const [reportFields, setReportFields] = useState({
    playerName: true,
    email: true,
    academy: true,
    totalCharged: true,
    totalPaid: true,
    outstandingBalance: true,
    totalOutstandingBalance: true,
    status: true,
  });

  // Lazy-loaded raw data for report generation only
  const [reportDataLoaded, setReportDataLoaded] = useState(false);
  const [allReceipts, setAllReceipts] = useState<Receipt[]>([]);
  const [allPlayers, setAllPlayers] = useState<Player[]>([]);
  const [academies, setAcademies] = useState<Academy[]>([]);
  const [userMap, setUserMap] = useState<Map<string, User>>(new Map());

  // Migration state (fix academy IDs + recalculate stats)
  const [migrating, setMigrating] = useState(false);
  const [migrationResult, setMigrationResult] = useState<string | null>(null);

  // Orphan cleanup state
  const [cleaningOrphans, setCleaningOrphans] = useState(false);
  const [orphanCleanupResult, setOrphanCleanupResult] = useState<string | null>(null);

  // Generate month options for navigation (last 12 months)
  const monthOptions = useMemo(() => {
    const options: { value: string; label: string; startDate: Date; endDate: Date }[] = [];
    const n = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(n.getFullYear(), n.getMonth() - i, 1);
      const endDate = new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      options.push({ value, label, startDate: date, endDate });
    }
    return options;
  }, []);

  const selectedMonthLabel = useMemo(() => {
    const opt = monthOptions.find(m => m.value === selectedMonth);
    return opt?.label || selectedMonth;
  }, [selectedMonth, monthOptions]);

  // Reset report cache when organization changes
  useEffect(() => {
    setReportDataLoaded(false);
  }, [selectedOrganization?.id]);

  // Load currency setting
  useEffect(() => {
    if (!selectedOrganization?.id) return;
    getSettingsByOrganization(selectedOrganization.id).then(settings => {
      if (settings?.generalSettings?.currency) {
        setDefaultCurrency(settings.generalSettings.currency);
      }
    }).catch(() => {});
  }, [selectedOrganization?.id]);

  // Section 1: Real-time overview listener
  useEffect(() => {
    if (!selectedOrganization?.id) return;
    setOverviewLoading(true);
    const unsub = getOrgStatsRealtime(
      selectedOrganization.id,
      selectedAcademy?.id,
      (stats) => {
        setOverviewStats(stats);
        setOverviewLoading(false);
      }
    );
    return () => unsub();
  }, [selectedOrganization?.id, selectedAcademy?.id]);

  // Section 2: Load monthly data when month changes
  useEffect(() => {
    if (!selectedOrganization?.id) return;
    setMonthlyLoading(true);

    Promise.all([
      getMonthlyDebit(selectedOrganization.id, selectedMonth, selectedAcademy?.id),
      getMonthlyCredit(selectedOrganization.id, selectedMonth, selectedAcademy?.id),
      getMonthlyExpenses(selectedOrganization.id, selectedMonth, selectedAcademy?.id),
    ]).then(([debit, credit, expenses]) => {
      setMonthlyDebit(debit);
      setMonthlyCredit(credit);
      setMonthlyExpenses(expenses);
    }).catch(err => {
      console.error('Error loading monthly data:', err);
    }).finally(() => {
      setMonthlyLoading(false);
    });
  }, [selectedOrganization?.id, selectedAcademy?.id, selectedMonth]);

  // Month navigation
  const navigateMonth = (direction: 'prev' | 'next') => {
    const [y, m] = selectedMonth.split('-').map(Number);
    const d = new Date(y, m - 1 + (direction === 'next' ? 1 : -1), 1);
    setSelectedMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  // Computed monthly metrics
  const monthlyMetrics = useMemo(() => {
    const expectedRevenue = monthlyDebit?.items.reduce((sum, i) => sum + i.amount, 0) || 0;
    const collected = monthlyCredit?.items.reduce((sum, i) => sum + i.amount, 0) || 0;
    const expenses = monthlyExpenses?.items.reduce((sum, i) => sum + i.amount, 0) || 0;
    const profit = collected - expenses;
    return { expectedRevenue, collected, expenses, profit };
  }, [monthlyDebit, monthlyCredit, monthlyExpenses]);

  // Products chart data (from debit items)
  const productsData = useMemo(() => {
    if (!monthlyDebit?.items.length) return [];
    const map = new Map<string, { revenue: number; count: number }>();
    monthlyDebit.items.forEach(item => {
      const cur = map.get(item.productName) || { revenue: 0, count: 0 };
      map.set(item.productName, { revenue: cur.revenue + item.amount, count: cur.count + 1 });
    });
    return Array.from(map.entries())
      .map(([name, data]) => ({ name, revenue: data.revenue, count: data.count }))
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 8);
  }, [monthlyDebit]);

  // Payment methods chart data (from credit items)
  const paymentMethodsData = useMemo(() => {
    if (!monthlyCredit?.items.length) return [];
    const map = new Map<string, { amount: number; count: number }>();
    monthlyCredit.items.forEach(item => {
      const method = item.paymentMethod || 'Unknown';
      const cur = map.get(method) || { amount: 0, count: 0 };
      map.set(method, { amount: cur.amount + item.amount, count: cur.count + 1 });
    });
    const totalCollected = monthlyMetrics.collected;
    return Array.from(map.entries())
      .map(([method, data]) => ({
        method,
        amount: data.amount,
        count: data.count,
        percentage: totalCollected > 0 ? (data.amount / totalCollected) * 100 : 0,
      }))
      .sort((a, b) => b.amount - a.amount);
  }, [monthlyCredit, monthlyMetrics.collected]);

  // Lazy-load raw data for report
  const loadReportData = useCallback(async () => {
    if (reportDataLoaded || !selectedOrganization?.id) return;
    try {
      const [receiptsData, playersData, academiesData] = await Promise.all([
        getReceiptsByOrganization(selectedOrganization.id),
        getPlayersByOrganization(selectedOrganization.id),
        getAcademiesByOrganization(selectedOrganization.id),
      ]);
      setAllReceipts(receiptsData);
      setAllPlayers(playersData);
      setAcademies(academiesData);

      const userIds = Array.from(new Set(playersData.map(p => p.userId)));
      const users = new Map<string, User>();
      await Promise.all(
        userIds.map(async (userId) => {
          try {
            const user = await getUserById(userId);
            if (user) users.set(userId, user);
          } catch { }
        })
      );
      setUserMap(users);
      setReportDataLoaded(true);
    } catch (err) {
      console.error('Error loading report data:', err);
    }
  }, [selectedOrganization?.id, reportDataLoaded]);

  const openReportModal = () => {
    setShowReportModal(true);
    loadReportData();
  };

  // Combined handler: fix missing academyId + recalculate all stats
  const handleFixAndRecalculate = async () => {
    if (!selectedOrganization?.id || migrating) return;
    setMigrating(true);
    setMigrationResult(null);
    try {
      const res = await migrateAcademyIds(selectedOrganization.id, (msg) => {
        setMigrationResult(msg);
      });
      setMigrationResult(
        `Done! Receipts fixed: ${res.receiptsFixed}, Transactions fixed: ${res.transactionsFixed}, Skipped: ${res.skipped}${res.errors.length > 0 ? `, Errors: ${res.errors.length}` : ''}`
      );
    } catch (err) {
      console.error('Fix & recalculate error:', err);
      setMigrationResult('Operation failed. Check console.');
    } finally {
      setMigrating(false);
    }
  };

  const handleCleanOrphanedReceipts = async () => {
    if (!selectedOrganization?.id || cleaningOrphans) return;
    setCleaningOrphans(true);
    setOrphanCleanupResult(null);
    try {
      const res = await cleanOrphanedReceipts(selectedOrganization.id, (msg) => {
        setOrphanCleanupResult(msg);
      });
      setOrphanCleanupResult(
        `Done! Orphaned users: ${res.orphanedUsers}, Receipts deleted: ${res.receiptsDeleted}`
      );
      setReportDataLoaded(false);
    } catch (err) {
      console.error('Orphan cleanup error:', err);
      setOrphanCleanupResult('Cleanup failed. Check console.');
    } finally {
      setCleaningOrphans(false);
    }
  };

  const reportStatusOptions = [
    { value: 'all', label: 'All Players' },
    { value: 'outstanding', label: 'With Outstanding Balance' },
    { value: 'paid', label: 'Fully Paid' },
  ];

  // Generate CSV report (kept as-is, uses lazy-loaded raw data)
  const generateReport = () => {
    setGeneratingReport(true);
    try {
      const selectedMonthData = monthOptions
        .filter(m => selectedMonths.includes(m.value))
        .sort((a, b) => b.startDate.getTime() - a.startDate.getTime());

      const fieldConfig: { key: keyof typeof reportFields; header: string; getValue: (p: { playerName: string; email: string; academy: string; totalCharged: number; totalPaid: number; outstandingBalance: number; totalOutstandingBalance: number; status: string }) => string }[] = [
        { key: 'playerName', header: 'Player Name', getValue: (p) => p.playerName },
        { key: 'email', header: 'Email', getValue: (p) => p.email },
        { key: 'academy', header: 'Academy', getValue: (p) => p.academy },
        { key: 'totalCharged', header: 'Total Charged', getValue: (p) => p.totalCharged.toFixed(2) },
        { key: 'totalPaid', header: 'Total Paid', getValue: (p) => p.totalPaid.toFixed(2) },
        { key: 'outstandingBalance', header: 'Outstanding Balance', getValue: (p) => p.outstandingBalance.toFixed(2) },
        { key: 'totalOutstandingBalance', header: 'Total Outstanding Balance', getValue: (p) => p.totalOutstandingBalance.toFixed(2) },
        { key: 'status', header: 'Status', getValue: (p) => p.status },
      ];

      const selectedFields = fieldConfig.filter(f => reportFields[f.key]);
      const headers = selectedFields.map(f => f.header);
      const csvRows: string[] = [];

      const orgName = selectedOrganization?.name || 'Organization';
      csvRows.push(`"${orgName} - Player Financial Report"`);
      csvRows.push(`"Generated on:","${new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}"`);
      csvRows.push('');

      selectedMonthData.forEach((month, monthIndex) => {
        const monthReceipts = allReceipts.filter(r => {
          if (r.status === 'deleted') return false;
          // Filter by selected academy if one is chosen
          if (selectedAcademy?.id && r.academyId !== selectedAcademy.id) return false;
          // Use the same date fields as the stats aggregation:
          // debit receipts use product.invoiceDate, credit receipts use paymentDate
          const date = r.type === 'debit' && r.product?.invoiceDate
            ? r.product.invoiceDate.toDate()
            : r.paymentDate?.toDate() || r.createdAt?.toDate();
          if (!date) return false;
          return date >= month.startDate && date <= month.endDate;
        });

        // Filter players by academy when one is selected
        const filteredPlayers = selectedAcademy?.id
          ? allPlayers.filter(p => p.academyId?.includes(selectedAcademy.id))
          : allPlayers;

        const monthPlayerData = filteredPlayers.map(player => {
          const user = userMap.get(player.userId);
          const playerReceipts = monthReceipts.filter(r => r.userRef?.id === player.userId);
          const debitReceipts = playerReceipts.filter(r => r.type === 'debit');
          const creditReceipts = playerReceipts.filter(r => r.type === 'credit');
          const totalCharged = debitReceipts.reduce((sum, r) => sum + r.amount, 0);
          // Total Paid = actual payments received during this month (matches dashboard "Collected")
          const totalPaid = creditReceipts.reduce((sum, r) => sum + r.amount, 0);
          const outstandingBalance = totalCharged - totalPaid;
          const status = outstandingBalance > 0 ? 'Outstanding' : outstandingBalance < 0 ? 'Credit' : 'Paid';
          const playerAcademyIds = player.academyId || [];
          const playerAcademyNames = academies
            .filter(a => playerAcademyIds.includes(a.id))
            .map(a => a.name)
            .join(', ') || 'No Academy';

          // Calculate total outstanding balance (all-time) for this player
          const allPlayerReceipts = allReceipts.filter(r => {
            if (r.status === 'deleted') return false;
            if (selectedAcademy?.id && r.academyId !== selectedAcademy.id) return false;
            return r.userRef?.id === player.userId;
          });
          const allPlayerDebits = allPlayerReceipts.filter(r => r.type === 'debit' && r.status !== 'completed');
          const allPlayerCredits = allPlayerReceipts.filter(r => r.type === 'credit');
          let totalOutstandingBalance = 0;
          for (const debit of allPlayerDebits) {
            const linkedCredits = allPlayerCredits.filter(c =>
              c.siblingReceiptRefs?.some(ref => ref.id === debit.id)
            );
            const creditsApplied = linkedCredits.reduce((sum, c) => sum + c.amount, 0);
            totalOutstandingBalance += Math.max(0, debit.amount - creditsApplied);
          }

          return { playerName: user?.name || user?.email || player.userId, email: user?.email || '', academy: playerAcademyNames, totalCharged, totalPaid, outstandingBalance, totalOutstandingBalance, status };
        });

        let monthReportData = monthPlayerData;
        if (reportStatus === 'outstanding') {
          monthReportData = monthPlayerData.filter(p => p.outstandingBalance > 0);
        } else if (reportStatus === 'paid') {
          monthReportData = monthPlayerData.filter(p => p.outstandingBalance <= 0);
        }
        monthReportData = monthReportData.filter(p => p.totalCharged > 0 || p.totalPaid > 0);
        monthReportData.sort((a, b) => b.outstandingBalance - a.outstandingBalance);

        const monthTotalCharged = monthReportData.reduce((sum, p) => sum + p.totalCharged, 0);
        const monthTotalPaid = monthReportData.reduce((sum, p) => sum + p.totalPaid, 0);
        const monthTotalOutstanding = monthReportData.reduce((sum, p) => sum + p.outstandingBalance, 0);
        const monthTotalAllTimeOutstanding = monthReportData.reduce((sum, p) => sum + p.totalOutstandingBalance, 0);

        csvRows.push('');
        csvRows.push(`"--- ${month.label.toUpperCase()} ---"`);
        csvRows.push('');

        if (monthReportData.length > 0) {
          csvRows.push(headers.map(h => `"${h}"`).join(','));
          monthReportData.forEach(p => {
            const row = selectedFields.map(f => `"${f.getValue(p)}"`).join(',');
            csvRows.push(row);
          });
          csvRows.push('');
          csvRows.push(`"Month Summary:","${month.label}"`);
          csvRows.push(`"Players:","${monthReportData.length}"`);
          csvRows.push(`"Total Charged:","${defaultCurrency} ${monthTotalCharged.toFixed(2)}"`);
          csvRows.push(`"Total Paid:","${defaultCurrency} ${monthTotalPaid.toFixed(2)}"`);
          csvRows.push(`"Outstanding:","${defaultCurrency} ${monthTotalOutstanding.toFixed(2)}"`);
          csvRows.push(`"Total Outstanding (All Time):","${defaultCurrency} ${monthTotalAllTimeOutstanding.toFixed(2)}"`);
        } else {
          csvRows.push(`"No data for this month"`);
        }

        if (monthIndex < selectedMonthData.length - 1) {
          csvRows.push('');
        }
      });

      // Overall balance section — iterates ALL receipts (not grouped by userRef)
      // to match recalculateAllStats which uses Firestore-path userId (never misses receipts)
      const allTimeReceipts = allReceipts.filter(r => {
        if (r.status === 'deleted') return false;
        if (selectedAcademy?.id && r.academyId !== selectedAcademy.id) return false;
        return true;
      });

      const allDebits = allTimeReceipts.filter(r => r.type === 'debit');
      const allCredits = allTimeReceipts.filter(r => r.type === 'credit');

      const overallTotalCharged = allDebits.reduce((sum, r) => sum + r.amount, 0);
      const overallTotalPaid = allCredits.reduce((sum, r) => sum + r.amount, 0);
      const usersWithActivity = new Set(
        allTimeReceipts.map(r => r.userRef?.id).filter(Boolean)
      ).size;

      // Compute outstanding using per-debit remaining balance (same as recalculateAllStats)
      let overallTotalOutstanding = 0;
      for (const debit of allDebits) {
        // Skip settled/completed debits (recalculateAllStats skips these)
        if (debit.status === 'completed') continue;

        // Find credits linked to this debit via siblingReceiptRefs
        const linkedCredits = allCredits.filter(c =>
          c.siblingReceiptRefs?.some(ref => ref.id === debit.id)
        );
        const creditsApplied = linkedCredits.reduce((sum, c) => sum + c.amount, 0);
        const remaining = Math.max(0, debit.amount - creditsApplied);
        overallTotalOutstanding += remaining;
      }

      csvRows.push('');
      csvRows.push('');
      csvRows.push(`"=== OVERALL BALANCE (ALL TIME) ==="`);
      csvRows.push(`"Total Charged:","${defaultCurrency} ${overallTotalCharged.toFixed(2)}"`);
      csvRows.push(`"Total Paid:","${defaultCurrency} ${overallTotalPaid.toFixed(2)}"`);
      csvRows.push(`"Total Outstanding:","${defaultCurrency} ${overallTotalOutstanding.toFixed(2)}"`);
      csvRows.push(`"Users with activity:","${usersWithActivity}"`);

      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      const periodLabel = selectedMonths.length === 1
        ? monthOptions.find(m => m.value === selectedMonths[0])?.label || selectedMonths[0]
        : `${selectedMonths.length}-months`;
      const statusLabel = reportStatusOptions.find(o => o.value === reportStatus)?.label || reportStatus;
      const dateStr = new Date().toISOString().split('T')[0];

      link.setAttribute('href', url);
      link.setAttribute('download', `Player-Financial-Report_${periodLabel}_${statusLabel}_${dateStr}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setShowReportModal(false);
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Failed to generate report. Please try again.');
    } finally {
      setGeneratingReport(false);
    }
  };

  if (!selectedOrganization) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <div className="text-center">
          <p className="text-secondary-500">No organization selected</p>
          <p className="text-sm text-secondary-400">Please select an organization to view stats</p>
        </div>
      </div>
    );
  }

  const outstanding = (overviewStats?.overdue || 0) + (overviewStats?.pending || 0);

  return (
    <div className="space-y-6">
      {/* Section 1: Overview Cards */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h3 className="text-lg font-semibold text-secondary-900">Financial Overview</h3>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={handleFixAndRecalculate}
            disabled={migrating}
          >
            {migrating ? 'Processing...' : 'Fix & Recalculate'}
          </Button>
          <Button
            variant="outline"
            onClick={handleCleanOrphanedReceipts}
            disabled={cleaningOrphans}
          >
            {cleaningOrphans ? 'Cleaning...' : 'Clean Orphaned Receipts'}
          </Button>
          <Button variant="outline" onClick={openReportModal}>
            Download Report
          </Button>
        </div>
      </div>

      {/* Migration progress / result banner */}
      {migrationResult && (
        <div className={`p-3 rounded-lg text-sm ${migrating ? 'bg-blue-50 text-blue-700' : 'bg-green-50 text-green-700'}`}>
          {migrationResult}
          {!migrating && (
            <button onClick={() => setMigrationResult(null)} className="ml-2 underline text-xs">dismiss</button>
          )}
        </div>
      )}

      {/* Orphan cleanup progress / result banner */}
      {orphanCleanupResult && (
        <div className={`p-3 rounded-lg text-sm ${cleaningOrphans ? 'bg-blue-50 text-blue-700' : 'bg-green-50 text-green-700'}`}>
          {orphanCleanupResult}
          {!cleaningOrphans && (
            <button onClick={() => setOrphanCleanupResult(null)} className="ml-2 underline text-xs">dismiss</button>
          )}
        </div>
      )}

      {overviewLoading ? (
        <div className="flex justify-center items-center min-h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="text-sm text-secondary-600">Overdue</div>
            <div className="text-2xl font-bold text-error-600">
              {defaultCurrency} {(overviewStats?.overdue || 0).toLocaleString()}
            </div>
            <div className="text-sm text-secondary-500 mt-1">Past deadline</div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-secondary-600">Pending</div>
            <div className="text-2xl font-bold text-warning-600">
              {defaultCurrency} {(overviewStats?.pending || 0).toLocaleString()}
            </div>
            <div className="text-sm text-secondary-500 mt-1">Awaiting payment</div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-secondary-600">Available Credit</div>
            <div className="text-2xl font-bold text-success-600">
              {defaultCurrency} {(overviewStats?.availableCredit || 0).toLocaleString()}
            </div>
            <div className="text-sm text-secondary-500 mt-1">Unlinked payments</div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-secondary-600">Outstanding Balance</div>
            <div className="text-2xl font-bold text-error-600">
              {defaultCurrency} {outstanding.toLocaleString()}
            </div>
            <div className="text-sm text-secondary-500 mt-1">Overdue + Pending</div>
          </Card>
        </div>
      )}

      {/* Section 2: Monthly Report with Navigator */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => navigateMonth('prev')}
            className="p-2 hover:bg-secondary-100 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5 text-secondary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h3 className="text-lg font-semibold text-secondary-900">{selectedMonthLabel}</h3>
          <button
            onClick={() => navigateMonth('next')}
            className="p-2 hover:bg-secondary-100 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5 text-secondary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {monthlyLoading ? (
          <div className="flex justify-center items-center min-h-32">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-secondary-50 rounded-lg p-4">
              <div className="text-sm text-secondary-600">Expected Revenue</div>
              <div className="text-xl font-bold text-primary-600">
                {defaultCurrency} {monthlyMetrics.expectedRevenue.toLocaleString()}
              </div>
            </div>
            <div className="bg-secondary-50 rounded-lg p-4">
              <div className="text-sm text-secondary-600">Collected</div>
              <div className="text-xl font-bold text-success-600">
                {defaultCurrency} {monthlyMetrics.collected.toLocaleString()}
              </div>
            </div>
            <div className="bg-secondary-50 rounded-lg p-4">
              <div className="text-sm text-secondary-600">Expenses</div>
              <div className="text-xl font-bold text-error-600">
                {defaultCurrency} {monthlyMetrics.expenses.toLocaleString()}
              </div>
            </div>
            <div className="bg-secondary-50 rounded-lg p-4">
              <div className="text-sm text-secondary-600">Profit</div>
              <div className={`text-xl font-bold ${monthlyMetrics.profit >= 0 ? 'text-success-600' : 'text-error-600'}`}>
                {defaultCurrency} {monthlyMetrics.profit.toLocaleString()}
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Section 3 & 4: Products & Payment Methods */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Products */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-secondary-900 mb-4">Products — {selectedMonthLabel}</h3>
          {monthlyLoading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
            </div>
          ) : productsData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={productsData} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tick={{ fontSize: 12 }} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={100} />
                <Tooltip
                  formatter={(value) => [`${defaultCurrency} ${Number(value || 0).toLocaleString()}`, 'Revenue']}
                />
                <Bar dataKey="revenue" fill="#3B82F6" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-8 text-secondary-500">
              No product data for this month
            </div>
          )}
        </Card>

        {/* Payment Methods */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-secondary-900 mb-4">Payment Methods — {selectedMonthLabel}</h3>
          {monthlyLoading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
            </div>
          ) : paymentMethodsData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={paymentMethodsData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={3}
                    dataKey="amount"
                    nameKey="method"
                    label={({ name, percent }) => `${name}: ${((percent || 0) * 100).toFixed(0)}%`}
                  >
                    {paymentMethodsData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`${defaultCurrency} ${Number(value || 0).toLocaleString()}`, '']} />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-4 space-y-2">
                {paymentMethodsData.map((method, index) => (
                  <div key={index} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                      <span className="text-secondary-700">{method.method}</span>
                    </div>
                    <span className="text-secondary-600">
                      {defaultCurrency} {method.amount.toLocaleString()} ({method.count})
                    </span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-8 text-secondary-500">
              No payment data for this month
            </div>
          )}
        </Card>
      </div>

      {/* Report Download Modal */}
      {showReportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md p-6 m-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-secondary-900">Download Player Financial Report</h3>
              <button onClick={() => setShowReportModal(false)} className="text-secondary-400 hover:text-secondary-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {!reportDataLoaded ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                <span className="ml-2 text-secondary-500">Loading report data...</span>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Month Selection */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-secondary-700">Select Months</label>
                    <button
                      type="button"
                      onClick={() => {
                        if (selectedMonths.length === monthOptions.length) {
                          setSelectedMonths([]);
                        } else {
                          setSelectedMonths(monthOptions.map(m => m.value));
                        }
                      }}
                      className="text-xs text-primary-600 hover:text-primary-700"
                    >
                      {selectedMonths.length === monthOptions.length ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>
                  <div className="bg-secondary-50 rounded-lg p-3 grid grid-cols-2 gap-2">
                    {monthOptions.slice(0, 6).map(month => (
                      <label key={month.value} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedMonths.includes(month.value)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedMonths(prev => [...prev, month.value]);
                            } else {
                              setSelectedMonths(prev => prev.filter(m => m !== month.value));
                            }
                          }}
                          className="w-4 h-4 text-primary-600 border-secondary-300 rounded focus:ring-primary-500"
                        />
                        <span className="text-sm text-secondary-700">{month.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Status Filter */}
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">Player Status</label>
                  <Select value={reportStatus} onChange={(e) => setReportStatus(e.target.value)} className="w-full">
                    {reportStatusOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </Select>
                </div>

                {/* Fields to Include */}
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">Fields to Include</label>
                  <div className="bg-secondary-50 rounded-lg p-3 space-y-2">
                    {[
                      { key: 'playerName', label: 'Player Name' },
                      { key: 'email', label: 'Email' },
                      { key: 'academy', label: 'Academy' },
                      { key: 'totalCharged', label: 'Total Charged' },
                      { key: 'totalPaid', label: 'Total Paid' },
                      { key: 'outstandingBalance', label: 'Outstanding Balance' },
                      { key: 'totalOutstandingBalance', label: 'Total Outstanding Balance (All Time)' },
                      { key: 'status', label: 'Payment Status' },
                    ].map(field => (
                      <label key={field.key} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={reportFields[field.key as keyof typeof reportFields]}
                          onChange={(e) => setReportFields(prev => ({ ...prev, [field.key]: e.target.checked }))}
                          className="w-4 h-4 text-primary-600 border-secondary-300 rounded focus:ring-primary-500"
                        />
                        <span className="text-sm text-secondary-700">{field.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end space-x-3 pt-2">
                  <Button variant="outline" onClick={() => setShowReportModal(false)}>Cancel</Button>
                  <Button
                    onClick={generateReport}
                    disabled={generatingReport || !Object.values(reportFields).some(v => v) || selectedMonths.length === 0}
                  >
                    {generatingReport ? 'Generating...' : 'Download CSV'}
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
};

export default Stats;
